import { useCallback, useEffect, useState } from "react";
import type { TFunction } from "i18next";

import { AVERAGE_SENSOR_UID } from "../IndoorClimate/SensorOverview";
import { useGraphControlState } from "./useGraphControlState";

/**
 * Encapsulates all UI state for the indoor climate page:
 * sensor selection, visibility toggles, date range, and resolution.
 */
export function useIndoorClimateSensorSelection() {
  const { dateRange, setDateRange, resolution, setResolution } = useGraphControlState();

  const [visibleSeries, setVisibleSeries] = useState<Record<string, boolean>>({
    temp: true,
    humid: true,
    outdoor: true,
    flex_temp_min: true,
    flex_temp_max: true,
    // Translucent band between min and max.
    flex_temp_range: true,
  });
  const [selectedIndividualSensors, setSelectedIndividualSensors] = useState<string[]>([]);
  const [selectedSensors, setSelectedSensors] = useState<string[]>([AVERAGE_SENSOR_UID]);

  // Reset sensor selection when both temp and humidity are hidden
  useEffect(() => {
    if (!visibleSeries.temp && !visibleSeries.humid) {
      setSelectedIndividualSensors([]);
      setSelectedSensors([AVERAGE_SENSOR_UID]);
    }
  }, [visibleSeries.temp, visibleSeries.humid]);

  useEffect(() => {
    const newVisibleSeries: Record<string, boolean> = {
      outdoor: visibleSeries["outdoor"],
      temp: visibleSeries["temp"],
      humid: visibleSeries["humid"],
      flex_temp_min: visibleSeries["flex_temp_min"] ?? true,
      flex_temp_max: visibleSeries["flex_temp_max"] ?? true,
      flex_temp_range: visibleSeries["flex_temp_range"] ?? true,
    };

    selectedSensors.forEach((uid) => {
      if (uid !== AVERAGE_SENSOR_UID) {
        // Temperature visibility logic
        let tempVisible: boolean;
        if (visibleSeries.temp === false) {
          tempVisible = false;
        } else if (selectedIndividualSensors.includes(uid)) {
          tempVisible = true;
        } else {
          tempVisible = visibleSeries[`temp_${uid}`] ?? true;
        }
        newVisibleSeries[`temp_${uid}`] = tempVisible;

        // Humidity visibility logic
        let humidVisible: boolean;
        if (visibleSeries.humid === false) {
          humidVisible = false;
        } else if (selectedIndividualSensors.includes(uid)) {
          humidVisible = true;
        } else {
          humidVisible = visibleSeries[`humid_${uid}`] ?? true;
        }
        newVisibleSeries[`humid_${uid}`] = humidVisible;
      }
    });

    setVisibleSeries(newVisibleSeries);
  }, [selectedSensors, visibleSeries.temp, visibleSeries.humid, selectedIndividualSensors]);

  const handleSeriesToggle = useCallback((seriesUid: string) => {
    setVisibleSeries((prev) => ({
      ...prev,
      [seriesUid]: !prev[seriesUid],
    }));
  }, []);

  const handleDisplayOptionChange = useCallback((key: string, checked: boolean) => {
    setVisibleSeries((prev) => ({ ...prev, [key]: checked }));
  }, []);

  const handleSensorSelect = useCallback(
    (sensorId: string) => {
      if (selectedIndividualSensors.includes(sensorId)) {
        setSelectedIndividualSensors(selectedIndividualSensors.filter((id) => id !== sensorId));
        setSelectedSensors(selectedSensors.filter((uid) => uid !== sensorId));
      } else {
        setSelectedIndividualSensors([...selectedIndividualSensors, sensorId]);
        setSelectedSensors([...selectedSensors, sensorId]);
      }
    },
    [selectedIndividualSensors, selectedSensors]
  );

  const handleSensorOverviewSelect = useCallback((sensors: string[]) => {
    setSelectedIndividualSensors(sensors);
    setSelectedSensors([AVERAGE_SENSOR_UID, ...sensors]);
  }, []);

  const getDisplayOptions = useCallback(
    (t: TFunction) => [
      { key: "temp", label: t("text_temperature", { ns: "_common" }), checked: visibleSeries.temp },
      { key: "humid", label: t("text_humidity", { ns: "_common" }), checked: visibleSeries.humid },
      {
        key: "flex_temp_min",
        label: `${t("text_temperature", { ns: "_common" })} (${t("text_minimum", { ns: "_common" })})`,
        checked: visibleSeries.flex_temp_min,
      },
      {
        key: "flex_temp_max",
        label: `${t("text_temperature", { ns: "_common" })} (${t("text_maximum", { ns: "_common" })})`,
        checked: visibleSeries.flex_temp_max,
      },
    ],
    [
      visibleSeries.temp,
      visibleSeries.humid,
      visibleSeries.flex_temp_min,
      visibleSeries.flex_temp_max,
    ]
  );

  return {
    resolution,
    setResolution,
    dateRange,
    setDateRange,
    visibleSeries,
    selectedIndividualSensors,
    selectedSensors,
    getDisplayOptions,
    handleSensorSelect,
    handleSensorOverviewSelect,
    handleSeriesToggle,
    handleDisplayOptionChange,
  };
}
